import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';

import { AuthenticationService } from '../authentication/authentication.service';
import { IoService } from '../io/io.service';

export type ThemeKey = 'redFire' | 'blueIce' | 'dark' | 'light';

export interface UserSettings {
  theme?: ThemeKey;
}

const THEME_CLASS_PREFIX = 'theme-';
const VALID_THEMES: ThemeKey[] = ['redFire', 'blueIce', 'dark', 'light'];

@Injectable({
  providedIn: 'root'
})
export class UserSettingsService {

  private settings: UserSettings = {};
  private settingsChanged = new Subject<UserSettings>();
  private themeChanged = new Subject<ThemeKey>();
  private authenticationStateChanged: Subscription;

  constructor(
    private authenticationService: AuthenticationService,
    private ioService: IoService
  ) {
    // Always set a default theme class on the body immediately
    this.applyTheme('light');

    this.authenticationStateChanged = authenticationService.authenticationStateChangedObservable().subscribe(authenticatedId => {
      if (authenticatedId) {
        this.loadSettings();
      } else {
        this.settings = {};
        this.applyTheme('light');
      }
    });

    if (authenticationService.isSignedIn()) {
      this.loadSettings();
    }
  }

  private async loadSettings() {
    try {
      const settings = await this.ioService.post('/settings/get') as UserSettings;
      this.settings = settings || {};
      this.applyTheme(this.settings.theme || 'light');
      this.settingsChanged.next(this.settings);
    } catch (e) {
      console.error('Failed to load user settings:', e);
      this.settings = {};
      this.applyTheme('light');
    }
  }

  getSettings(): UserSettings {
    return this.settings;
  }

  getTheme(): ThemeKey {
    return this.settings.theme || 'light';
  }

  async setTheme(theme: ThemeKey) {
    if (VALID_THEMES.indexOf(theme) === -1) {
      return;
    }

    this.settings.theme = theme;
    this.applyTheme(theme);

    try {
      await this.ioService.post('/settings/update', {
        settings: { theme }
      });
    } catch (e) {
      console.error('Failed to save theme setting:', e);
    }

    this.settingsChanged.next(this.settings);
  }

  async updateSettings(updates: Partial<UserSettings>) {
    Object.assign(this.settings, updates);

    if (updates.theme) {
      this.applyTheme(updates.theme);
    }

    try {
      await this.ioService.post('/settings/update', {
        settings: updates
      });
    } catch (e) {
      console.error('Failed to save settings:', e);
    }

    this.settingsChanged.next(this.settings);
  }

  settingsChangedObservable(): Observable<UserSettings> {
    return this.settingsChanged.asObservable();
  }

  themeChangedObservable(): Observable<ThemeKey> {
    return this.themeChanged.asObservable();
  }

  private applyTheme(theme: ThemeKey) {
    const body = document.body;
    const classes = body.className.split(' ').filter(c => !c.startsWith(THEME_CLASS_PREFIX));
    classes.push(THEME_CLASS_PREFIX + theme);
    body.className = classes.join(' ').trim();
    this.themeChanged.next(theme);
  }
}
